import { Injectable, Inject, Optional } from "@angular/core";

import { first } from "rxjs/operators";

import { DffCurrentRoute, DffRedirectService } from "@dffedb/router";

import { UmbracoIndstillingerObserver } from "../umbraco-indstillinger";
import { getLoginRouteByPreference, isTeknikerContext } from "../util";
import { APP_SHELL_LAYOUT_OPTIONS, AppShellLayoutOptions } from "../app-shell/app-shell-layout.token";

@Injectable({ providedIn: "root" })
export class RedirectService {
    constructor(
        private readonly redirectService: DffRedirectService,
        private readonly currentRoute: DffCurrentRoute,
        private readonly indstillinger: UmbracoIndstillingerObserver,
        @Optional() @Inject(APP_SHELL_LAYOUT_OPTIONS) private readonly layoutOptions: AppShellLayoutOptions | null
    ) {}

    public redirectTilDefault(): void {
        this.redirectService.redirect("/");
    }

    public redirectTilLogin(redirectTilAktuelleRouteEfterLogin: boolean = false): void {
        const redirectUrl = redirectTilAktuelleRouteEfterLogin ? this.currentRoute.value.path : "";
        const configured = this.layoutOptions?.teknikerLoginRoute?.trim();
        const preferredLoginRoute = configured
            ? configured.startsWith("/")
                ? configured
                : `/${configured}`
            : getLoginRouteByPreference(localStorage.getItem("sidste-login-side"), isTeknikerContext());
        this.redirectService.redirect(`${preferredLoginRoute}${redirectUrl}`);
    }

    public redirectTilFlyttekvittering(parameter: any): void {
        this.redirectService.redirect("/meld-flytning/kvittering", { data: btoa(JSON.stringify(parameter)) });
    }

    public redirectTilInstallationer(): void {
        this.redirectService.redirect("/install-picker");
    }

    public redirectTilDefaultLanding(): void {
        const route = this.layoutOptions?.defaultLandingRoute;
        if (route) {
            this.redirectService.redirect(route);
            return;
        }
        this.redirectTilInstallationer();
    }

    public redirectTilMitForbrug(): void {
        this.redirectService.redirect("/mit-forbrug");
    }

    public redirectTilMineDokumenter(): void {
        this.redirectService.redirect("/mine-dokumenter");
    }

    public redirect(path: string, params?: any): void {
        this.redirectService.redirect(path, params);
    }

    private removeStartUrlFromIndexedDb(): Promise<void> {
        return new Promise((resolve) => {
            if (typeof indexedDB === "undefined") return resolve();

            const DB_NAME = "vaerkslisteDB";
            const SETTINGS_STORE = "settings";
            let req: IDBOpenDBRequest;

            try {
                req = indexedDB.open(DB_NAME); // no version -> no forced upgrade
            } catch {
                return resolve();
            }

            req.onerror = () => resolve();

            req.onsuccess = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
                    db.close();
                    return resolve();
                }
                try {
                    const tx = db.transaction(SETTINGS_STORE, "readwrite");
                    const store = tx.objectStore(SETTINGS_STORE);
                    store.delete("startUrl");
                    tx.oncomplete = () => {
                        db.close();
                        resolve();
                    };
                    tx.onerror = () => {
                        db.close();
                        resolve();
                    };
                    tx.onabort = () => {
                        db.close();
                        resolve();
                    };
                } catch {
                    db.close();
                    resolve();
                }
            };
        });
    }

    /**
     * Clears all plant-related data from both localStorage and IndexedDB
     * This ensures complete cleanup when redirecting to værksliste
     */
    private clearAllPlantData(): Promise<void> {
        return new Promise((resolve) => {
            // Clear localStorage immediately
            localStorage.removeItem("chosenPlant");
            localStorage.removeItem("lastPlantSelectionTime");

            // Clear IndexedDB asynchronously
            this.removeStartUrlFromIndexedDb()
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    resolve();
                });
        });
    }

    public redirectTilVaerksListe(): void {
        this.indstillinger.valueChanges
            .pipe(first()) // Auto-unsubscribe
            .subscribe((indstillinger) => {
                let url = indstillinger.vaerkslisteUrl;

                // Add clear=true parameter to indicate storage should be cleared
                const separator = url.includes("?") ? "&" : "?";
                url = `${url}${separator}clear=true`;

                location.href = url;
            });
    }
}
